import { formatCompactCurrency } from "./formatCompactCurrency";
import { WorldMapAccount } from "../../Components/WorldMap";
import { ActiveCustomerWithSponsorsItem } from "../../Store/Reducers/OpsSlice";

/**
 * Transform API account data to WorldMap account format
 */
export const transformAccountsForWorldMap = (
  accounts: ActiveCustomerWithSponsorsItem[]
): WorldMapAccount[] => {
  return accounts.map(account => {
    // Map churn_risk → map status (high=Critical, medium=At Risk, low=Healthy)
    let status: WorldMapAccount["status"] = "unassigned";
    const churnRisk = String(
      account.churn_risk || (account as { churnRisk?: string }).churnRisk || ""
    )
      .trim()
      .toLowerCase();

    if (churnRisk === "high") {
      status = "critical";
    } else if (churnRisk === "medium") {
      status = "at-risk";
    } else if (churnRisk === "low") {
      status = "healthy";
    }

    // Format revenue
    const revenue = formatCompactCurrency(account.revenue, { fallback: "N/A" });

    // Build location string from city and country
    const locationParts: string[] = [];
    if (account.city) locationParts.push(account.city);
    if (account.country) locationParts.push(account.country);
    const location =
      locationParts.length > 0
        ? locationParts.join(", ")
        : account.region || account.industry_location || "Unknown";

    return {
      name: account.name || "Unknown",
      status,
      location,
      latitude: account.latitude || null, // Will be derived from location if not available
      longitude: account.longitude || null, // Will be derived from location if not available
      revenue,
      csat: "N/A", // Not available in ActiveCustomerWithSponsorsItem
      tier: account.tiers || "Unknown",
      sponsor: account.primary_sponsors || "Unassigned",
      lastTouch: account.last_level_date || "N/A",
    };
  });
};

/**
 * Get account counts grouped by status
 */
export const getAccountCountsByStatus = (
  accounts: WorldMapAccount[]
): {
  healthy: number;
  atRisk: number;
  critical: number;
  unassigned: number;
  total: number;
} => {
  const counts = {
    healthy: 0,
    atRisk: 0,
    critical: 0,
    unassigned: 0,
    total: accounts.length,
  };

  accounts.forEach(account => {
    const status = account.status?.toLowerCase();
    if (status === "healthy") {
      counts.healthy++;
    } else if (status === "at-risk" || status === "at risk") {
      counts.atRisk++;
    } else if (status === "critical") {
      counts.critical++;
    } else {
      counts.unassigned++;
    }
  });

  return counts;
};
